import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { requestEstimate } from "@/lib/api";
import { Sparkles, Loader2, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PROJECT_TYPES = [
  { v: "landing", label: "Landing page" },
  { v: "website", label: "Marketing website" },
  { v: "product", label: "Product / SaaS UI" },
  { v: "brand", label: "Brand identity" },
  { v: "other", label: "Something else" },
];

const COMPLEXITY = [
  { v: "simple", label: "Simple", d: "Clean and focused" },
  { v: "standard", label: "Standard", d: "Multiple sections, animations" },
  { v: "advanced", label: "Advanced", d: "CMS, integrations, custom" },
];

const FEATURES = [
  "Animations & motion",
  "CMS / editable content",
  "E-commerce / checkout",
  "Auth / user accounts",
  "Dashboards / data viz",
  "AI features",
  "Multi-language",
  "Custom illustrations",
];

export default function Estimator() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    project_type: "website",
    complexity: "standard",
    pages_or_screens: 5,
    timeline_weeks: 4,
    features: [],
    description: "",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFeature = (feat) =>
    setForm((f) => ({
      ...f,
      features: f.features.includes(feat) ? f.features.filter((x) => x !== feat) : [...f.features, feat],
    }));

  const handleSubmit = async () => {
    if (!form.description.trim()) {
      toast.error("Add a short description to get a better estimate.");
      return;
    }
    setLoading(true);
    try {
      const res = await requestEstimate(form);
      setResult(res);
      setStep(4);
    } catch (e) {
      toast.error("Something went wrong. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setStep(0);
  };

  const steps = ["Type", "Scope", "Features", "Brief"];

  return (
    <section
      id="estimator"
      data-testid="estimator-section"
      className="relative py-24 md:py-32 lg:py-40 border-t border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-20">
          <div className="md:col-span-4">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-indigo-400 flex items-center gap-2">
              <Sparkles size={12} /> 05 / AI Estimator
            </p>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white text-balance">
              Get a ballpark quote in 60 seconds.
            </h2>
            <p className="mt-4 max-w-2xl text-base lg:text-lg text-zinc-400 leading-relaxed">
              Answer four quick questions. My AI assistant will return a realistic scope, timeline and price range — no email required.
            </p>
          </div>
        </div>

        <div className="relative rounded-3xl border border-indigo-500/20 bg-gradient-to-br from-indigo-500/[0.04] via-black to-black p-6 md:p-10 lg:p-14 ai-glow overflow-hidden">
          {/* Progress */}
          {step < 4 && (
            <div className="flex items-center gap-2 mb-10">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-0.5 flex-1 rounded-full transition-colors ${
                    i <= step ? "bg-indigo-400" : "bg-white/10"
                  }`}
                />
              ))}
              <span className="ml-3 font-mono text-xs uppercase tracking-widest text-zinc-500 shrink-0">
                {step < 4 ? `${step + 1} / 4` : "Result"}
              </span>
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div
                key="s0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35 }}
                data-testid="estimator-step-0"
              >
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                  What are we building?
                </h3>
                <RadioGroup
                  value={form.project_type}
                  onValueChange={(v) => setField("project_type", v)}
                  className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  {PROJECT_TYPES.map((t) => (
                    <label
                      key={t.v}
                      htmlFor={`pt-${t.v}`}
                      data-testid={`estimator-type-${t.v}`}
                      className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition ${
                        form.project_type === t.v
                          ? "border-indigo-400 bg-indigo-500/5"
                          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                      }`}
                    >
                      <RadioGroupItem value={t.v} id={`pt-${t.v}`} className="border-white/30 text-indigo-400" />
                      <span className="text-white font-medium">{t.label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35 }}
                data-testid="estimator-step-1"
              >
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                  How complex, and how many pages / screens?
                </h3>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
                  {COMPLEXITY.map((c) => (
                    <button
                      key={c.v}
                      type="button"
                      data-testid={`estimator-complexity-${c.v}`}
                      onClick={() => setField("complexity", c.v)}
                      className={`text-left rounded-xl border p-5 transition ${
                        form.complexity === c.v
                          ? "border-indigo-400 bg-indigo-500/5"
                          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                      }`}
                    >
                      <div className="font-display text-lg text-white font-semibold">{c.label}</div>
                      <div className="mt-1 text-sm text-zinc-400">{c.d}</div>
                    </button>
                  ))}
                </div>

                <div className="mt-10">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-zinc-300">Pages / screens</Label>
                    <span className="font-mono text-sm text-indigo-300" data-testid="estimator-pages-value">
                      {form.pages_or_screens}
                    </span>
                  </div>
                  <Slider
                    data-testid="estimator-pages-slider"
                    value={[form.pages_or_screens]}
                    onValueChange={(v) => setField("pages_or_screens", v[0])}
                    min={1}
                    max={30}
                    step={1}
                    className="mt-4"
                  />
                </div>

                <div className="mt-8">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-zinc-300">Preferred timeline (weeks)</Label>
                    <span className="font-mono text-sm text-indigo-300" data-testid="estimator-timeline-value">
                      {form.timeline_weeks}w
                    </span>
                  </div>
                  <Slider
                    data-testid="estimator-timeline-slider"
                    value={[form.timeline_weeks]}
                    onValueChange={(v) => setField("timeline_weeks", v[0])}
                    min={1}
                    max={16}
                    step={1}
                    className="mt-4"
                  />
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="s2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35 }}
                data-testid="estimator-step-2"
              >
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                  Any of these on the wishlist?
                </h3>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {FEATURES.map((f) => (
                    <label
                      key={f}
                      data-testid={`estimator-feature-${f.split(' ')[0].toLowerCase()}`}
                      className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition ${
                        form.features.includes(f)
                          ? "border-indigo-400 bg-indigo-500/5"
                          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                      }`}
                    >
                      <Checkbox
                        checked={form.features.includes(f)}
                        onCheckedChange={() => toggleFeature(f)}
                        className="border-white/30 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                      />
                      <span className="text-white text-sm">{f}</span>
                    </label>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="s3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35 }}
                data-testid="estimator-step-3"
              >
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                  In your own words — what are you making?
                </h3>
                <Textarea
                  data-testid="estimator-description"
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  placeholder="e.g. A 6-page marketing site for our climate tech startup. We need a hero video, blog, careers page, and integrations page. Launch in 5 weeks."
                  className="mt-6 min-h-[160px] bg-transparent border-white/10 focus:border-indigo-400 text-white placeholder:text-zinc-500 rounded-xl"
                />
              </motion.div>
            )}

            {step === 4 && result && (
              <motion.div
                key="s4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                data-testid="estimator-result"
              >
                <div className="flex items-center gap-2 text-indigo-300 mb-4">
                  <CheckCircle2 size={18} />
                  <span className="font-mono text-xs uppercase tracking-widest">Draft estimate</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Price range</div>
                    <div
                      className="mt-2 font-display text-3xl md:text-4xl font-bold text-white"
                      data-testid="estimator-price"
                    >
                      ${result.price_low.toLocaleString()} — ${result.price_high.toLocaleString()}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Effort</div>
                    <div className="mt-2 font-display text-3xl md:text-4xl font-bold text-white">
                      {result.estimated_hours}h
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Timeline</div>
                    <div className="mt-2 font-display text-3xl md:text-4xl font-bold text-white">
                      {result.timeline || "—"}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8">
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Included scope</div>
                  <ul className="space-y-3">
                    {result.scope?.map((s, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-zinc-200" data-testid={`estimator-scope-${i}`}>
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                  {result.notes && (
                    <p className="mt-6 text-sm text-zinc-400 italic border-l border-white/10 pl-4">
                      {result.notes}
                    </p>
                  )}
                </div>

                <div className="mt-10 flex flex-wrap gap-3">
                  <a
                    href="https://calendly.com/alixwilson112009/new-meeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="estimator-book-call"
                    className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white px-6 py-3.5 text-sm font-medium transition active:scale-[0.98] ai-glow"
                  >
                    <Sparkles size={16} />
                    Book a free 15-min discovery call
                  </a>
                  <a
                    href="#contact"
                    data-testid="estimator-contact-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:bg-zinc-200 transition active:scale-[0.98]"
                  >
                    Send this brief
                    <ArrowRight size={16} />
                  </a>
                  <button
                    type="button"
                    data-testid="estimator-restart"
                    onClick={reset}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 text-white px-6 py-3.5 text-sm hover:bg-white/10 transition"
                  >
                    Start over
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {step < 4 && (
            <div className="mt-10 flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                data-testid="estimator-back"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="text-zinc-400 hover:text-white hover:bg-white/5"
              >
                <ArrowLeft size={16} className="mr-2" /> Back
              </Button>

              {step < 3 ? (
                <Button
                  type="button"
                  data-testid="estimator-next"
                  onClick={() => setStep((s) => s + 1)}
                  className="rounded-full bg-white text-black hover:bg-zinc-200"
                >
                  Next <ArrowRight size={16} className="ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  data-testid="estimator-submit"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" /> Estimating...
                    </>
                  ) : (
                    <>
                      Get estimate <Sparkles size={16} className="ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
